/*
  hero-framing.js — product photographs whose subject sits low in the frame

  WHAT IT DOES, in one sentence: where a product's photographs put the object near the bottom with a
  lot of empty backdrop above it, show a shorter window anchored to the bottom of the photograph
  instead of the full square, so the object is not stranded in a large empty rectangle.

  Nothing about the photographs themselves is changed. This is a display decision only, taken in the
  visitor's browser, and it is reversed by deleting this file's <script> tag.

  ── HOW IT DECIDES ────────────────────────────────────────────────────────────────────────────────
  Empty backdrop is smooth and an object is not. So for each horizontal line of pixels the program
  adds up how much the brightness jumps between neighbours along that line. A line crossing plain
  backdrop barely changes; a line crossing the object changes constantly. The first line that rises
  clear of the background noise is the top of the object.

  Two conditions, and BOTH must hold or nothing happens:
    1. the FIRST photograph has at least 66% empty space above its object, and
    2. EVERY photograph in that product's gallery has its object starting below 30% of the frame,
       so that none of them loses its top when the window is shortened.
  Condition 2 exists so the window is the same shape for every photograph in the gallery, which
  means the box never changes height as the visitor clicks through the pictures.

  ── WHY IT FETCHES ITS OWN COPY OF EACH PICTURE ───────────────────────────────────────────────────
  🔴 MEASURED 2026-09-10, and this file exists in this shape because of it. An earlier version read
  the page's own <img> element for the picture's size. On this shop that element reported a natural
  width of 660 while the picture it had actually decoded was 713 wide. The measurement was therefore
  divided by the wrong number and reported 89.2% where the truth was 82.6% — a plausible, confident,
  wrong answer with nothing raised. Proven rather than assumed: the row-by-row profile the browser
  produced matched the 713-pixel file at 0.998 and the 660-pixel file at 0.408.

  So this file never asks the page how big a picture is. It requests a copy at a width it chooses,
  and REFUSES TO ACT unless what arrives is exactly that width. Every other uncertainty is treated
  the same way: if anything cannot be measured, the page is left exactly as it was.

  ── THE SIZE IT MEASURES ──────────────────────────────────────────────────────────────────────────
  200 pixels wide, about 5KB per picture, cached by the browser and remembered across visits.
  Measured across five widths from 120 to 660 on the eight products the shop owner had sorted by
  eye: every width separates their two groups with a gap of at least 27.5 percentage points, and
  every width sorts them correctly at the 66% line.
*/
(function () {
  'use strict';

  var EMPTY_ABOVE_LINE = 0.66;        // empty space above the object that counts as "stranded low"
  var SUBJECT_MUST_START_BELOW = 0.30; // no picture may have its object above this, or nothing runs
  var WINDOW_RATIO = 4 / 3;           // the window shape used instead of the square
  var SAMPLE_WIDTH = 200;             // the width we request, and the width we insist on receiving
  var DETAIL_THRESHOLD = 0.15;        // a line counts as "object" above this share of the range
  var FLOOR_PERCENTILE = 0.10;        // what an empty line looks like in THIS picture
  var STORE_PREFIX = 'heroFraming.v1.';

  /* A ran/didn't-run bit on the page itself. Without one, "the rule left this page alone" and "the
     rule never ran" look identical from outside, which is the failure this project calls SHAPE 8. */
  function mark(state) {
    try { document.documentElement.setAttribute('data-hero-framing', state); } catch (e) {}
  }

  function sampleUrl(raw) {
    if (!raw) return null;
    var base = String(raw).split('?')[0];
    if (base.indexOf('//') === -1) return null;
    return base + '?width=' + SAMPLE_WIDTH;
  }

  function remembered(url) {
    try {
      var v = window.localStorage.getItem(STORE_PREFIX + url);
      if (v === null) return undefined;              // never measured
      var n = parseFloat(v);
      return (n >= 0 && n <= 1) ? n : null;          // null = measured, and unmeasurable
    } catch (e) { return undefined; }
  }

  function remember(url, value) {
    try {
      window.localStorage.setItem(STORE_PREFIX + url, value === null ? 'unmeasurable' : String(value));
    } catch (e) { /* private window, or a full store. Not worth failing the page over. */ }
  }

  /* Resolves to the object's top edge as a fraction of the picture's height, or null for
     "could not measure this, so do not act on it". */
  function measure(url) {
    return new Promise(function (resolve) {
      var probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.onerror = function () { resolve(null); };
      probe.onload = function () {
        var w = probe.naturalWidth;
        var h = probe.naturalHeight;
        /* 🔴 The refusal that this whole file is built around: if what arrived is not the width we
           asked for, we do not know what we are looking at, so we do not act. */
        if (w !== SAMPLE_WIDTH || !(h >= 16)) { resolve(null); return; }

        var canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        var ctx = canvas.getContext('2d');
        var data;
        try {
          ctx.drawImage(probe, 0, 0, w, h);   // an explicit destination size, never the default
          data = ctx.getImageData(0, 0, w, h).data;
        } catch (e) {
          resolve(null); return;              // the picture service declined to let us read it
        }

        var strength = new Float64Array(h);
        for (var y = 0; y < h; y++) {
          var sum = 0, row = y * w * 4, prev = 0;
          for (var x = 0; x < w; x++) {
            var i = row + x * 4;
            var grey = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
            if (x > 0) sum += Math.abs(grey - prev);
            prev = grey;
          }
          strength[y] = sum / (w - 1);
        }

        var sorted = Array.prototype.slice.call(strength).sort(function (a, b) { return a - b; });
        var floor = sorted[Math.floor(sorted.length * FLOOR_PERCENTILE)];
        var peak = sorted[sorted.length - 1];
        if (!(peak - floor > 1e-6)) { resolve(null); return; }   // a blank picture has no object

        for (var y2 = 0; y2 < h; y2++) {
          if ((strength[y2] - floor) / (peak - floor) > DETAIL_THRESHOLD) {
            resolve(y2 / h); return;
          }
        }
        resolve(null);                                            // no object found at all
      };
      probe.src = url;
    });
  }

  function apply(containers, images) {
    for (var i = 0; i < containers.length; i++) {
      containers[i].style.setProperty('--preview-ratio', String(WINDOW_RATIO));
      images[i].style.objectPosition = '50% 100%';
    }
  }

  function run() {
    var wrapper = document.querySelector('.product__media-wrapper');
    if (!wrapper) { return; }                       // not a product page; say nothing at all
    var containers = wrapper.querySelectorAll('.product-media-container');
    if (!containers.length) { mark('no-pictures'); return; }

    var images = [], urls = [];
    for (var i = 0; i < containers.length; i++) {
      var img = containers[i].querySelector('img');
      var url = img && sampleUrl(img.getAttribute('src') || img.currentSrc || img.src);
      if (!url) { mark('unreadable'); return; }     // one picture we cannot name: leave all of them
      images.push(img);
      urls.push(url);
    }

    Promise.all(urls.map(function (url) {
      var known = remembered(url);
      if (known !== undefined) { return Promise.resolve(known); }
      return measure(url).then(function (value) { remember(url, value); return value; });
    })).then(function (tops) {
      for (var a = 0; a < tops.length; a++) {
        if (tops[a] === null) { mark('unmeasurable'); return; }
      }
      if (tops[0] < EMPTY_ABOVE_LINE) { mark('unchanged'); return; }
      for (var b = 0; b < tops.length; b++) {
        if (tops[b] < SUBJECT_MUST_START_BELOW) { mark('unchanged-mixed-gallery'); return; }
      }
      apply(containers, images);
      mark('bottom');
    })['catch'](function () { mark('failed'); });
  }

  /* The gallery is rebuilt when a visitor picks a different variant, which throws our styles away.
     Re-run when that happens, but never more than once per frame. */
  function watch() {
    var wrapper = document.querySelector('.product__media-wrapper');
    if (!wrapper || typeof MutationObserver !== 'function') { return; }
    var pending = false;
    new MutationObserver(function () {
      if (pending) { return; }
      pending = true;
      window.requestAnimationFrame(function () { pending = false; run(); });
    }).observe(wrapper, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { run(); watch(); });
  } else {
    run(); watch();
  }
})();
